import { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import ScheduleDialog, {
    ScheduleDateTimeFields,
    ScheduleErrorMessage,
    ScheduleOptionGroup,
    scheduleDialogButtonClassNames,
} from './crm.opportunity.schedule.dialog';

import useScheduleConfig from '../../../../hooks/crm/useScheduleConfig';

import crmOpportunityScheduleService from '../../../../domain/services/crm/crm.opportunity.schedule.service';
import {
    scheduleOutcomeOptions,
    scheduleOutcomes,
    scheduleTexts,
} from '../../../../domain/constant/crm/opportunity/crm.opportunity.schedule.constant';

const ScheduleConclusionModal = ({ isOpen, opportunity = {}, prefillMessage = '', origin = null, onClose, onConcluded }) => {

    const [outcome, setOutcome] = useState(null);
    const [message, setMessage] = useState('');
    const [nextDate, setNextDate] = useState('');
    const [nextTime, setNextTime] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const { enqueueSnackbar } = useSnackbar();
    const { config, status, reload } = useScheduleConfig(isOpen);

    useEffect(() => {
        if (!isOpen)
            return;

        setOutcome(null);
        setMessage(prefillMessage || '');
        setNextDate('');
        setNextTime('');
        setErrorMessage(null);
    }, [isOpen, prefillMessage]);

    const isLimitReached = crmOpportunityScheduleService.hasReachedScheduleLimit(opportunity);
    const selectedOutcome = isLimitReached ? scheduleOutcomes.needsConversation : outcome;

    const minimumChars = Number(config?.minConclusionChars || 0);
    const messageLength = message.trim().length;
    const isWillBuy = selectedOutcome === scheduleOutcomes.willBuy;
    const hasNextSchedule = !isWillBuy || (!!nextDate && !!nextTime);
    const canSubmit = status === 'ready' && !!selectedOutcome && messageLength >= minimumChars && hasNextSchedule && !isSubmitting;

    const submit = async () => {

        setIsSubmitting(true);
        setErrorMessage(null);

        try {
            const result = await crmOpportunityScheduleService.concludeSchedule(opportunity.id, {
                outcome: selectedOutcome,
                conclusionMessage: message.trim(),
                nextScheduledAt: isWillBuy ? crmOpportunityScheduleService.toLocalDateTime(nextDate, nextTime) : null,
                origin,
            });

            enqueueSnackbar(scheduleTexts.conclusionSuccessMessage, { variant: 'success' });
            onConcluded && onConcluded(result);
        } catch (error) {
            setErrorMessage(crmOpportunityScheduleService.getErrorMessage(error, 'Não foi possível concluir o atendimento.'));
        } finally {
            setIsSubmitting(false);
        }
    }

    const renderBody = () => {

        if (status === 'loading')
            return <div className="h-[260px] animate-pulse rounded-lg bg-gray-100" />;

        if (status === 'error')
            return (
                <div className="py-6 text-center text-[13.5px] text-gray-600">
                    {scheduleTexts.loadConfigError}
                    <button type="button" onClick={reload} className="ml-1 font-medium text-primary hover:underline">
                        {scheduleTexts.retryLabel}
                    </button>
                </div>
            );

        return (
            <>
                {isLimitReached ? (
                    <p className="mb-5 rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-3 text-[13px] text-gray-700">
                        {scheduleTexts.getLimitReachedConclusionNotice(config?.maxPerOpportunity)}
                    </p>
                ) : (
                    <ScheduleOptionGroup
                        name="schedule-outcome"
                        label={scheduleTexts.outcomeLabel}
                        options={scheduleOutcomeOptions}
                        value={outcome}
                        onChange={setOutcome}
                        disabled={isSubmitting}
                    />
                )}

                {isWillBuy && (
                    <div className="mb-4">
                        <ScheduleDateTimeFields
                            idPrefix="schedule-purchase"
                            dateLabel={scheduleTexts.nextDateLabel}
                            timeLabel={scheduleTexts.nextTimeLabel}
                            date={nextDate}
                            time={nextTime}
                            minDate={crmOpportunityScheduleService.getTodayDate()}
                            maxDate={config?.maxDate}
                            onDateChange={setNextDate}
                            onTimeChange={setNextTime}
                            disabled={isSubmitting}
                        />
                    </div>
                )}

                <label htmlFor="schedule-conclusion-message" className="mb-1.5 block text-[12.5px] font-medium text-gray-700">
                    {scheduleTexts.messageLabel}
                </label>
                <textarea
                    id="schedule-conclusion-message"
                    value={message}
                    disabled={isSubmitting}
                    placeholder={scheduleTexts.messagePlaceholder}
                    onChange={(event) => setMessage(event.target.value)}
                    className="form-textarea min-h-[110px] w-full resize-y rounded-lg border-gray-300 text-[13.5px] leading-normal focus:border-primary focus:ring-primary"
                />
                <p className={`mt-1 text-right text-[12px] ${messageLength >= minimumChars ? 'text-emerald-600' : 'text-gray-500'}`}>
                    {scheduleTexts.getMessageCounter(messageLength, minimumChars)}
                </p>

                <ScheduleErrorMessage message={errorMessage} />
            </>
        );
    }

    return (
        <ScheduleDialog
            isOpen={isOpen}
            title={scheduleTexts.conclusionTitle}
            description={scheduleTexts.conclusionDescription}
            onClose={onClose}
            isBusy={isSubmitting}
            footer={(
                <>
                    <button type="button" onClick={onClose} disabled={isSubmitting} className={scheduleDialogButtonClassNames.secondary}>
                        {scheduleTexts.cancelLabel}
                    </button>
                    <button type="button" onClick={submit} disabled={!canSubmit} className={scheduleDialogButtonClassNames.primary}>
                        {isSubmitting ? scheduleTexts.conclusionSubmittingLabel : scheduleTexts.conclusionSubmitLabel}
                    </button>
                </>
            )}
        >
            {renderBody()}
        </ScheduleDialog>
    );
};

export default ScheduleConclusionModal;
